import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, Clock, BookOpen, ArrowUpRight } from "lucide-react";
import Countdown from "@/components/Countdown";
import { money } from "@/lib/api";

export default function CourseCard({ course }) {
  const nav = useNavigate();
  const discount = course.discount_percentage || 0;
  const openCourse = (e) => {
    // Don't intercept if user clicked a link/button inside the card
    if (e.target.closest("a,button")) return;
    nav(`/courses/${course.slug}`);
  };
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav(`/courses/${course.slug}`); }
  };
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={openCourse}
      onKeyDown={onKey}
      className="group relative rounded-2xl overflow-hidden glass hover:border-gold/40 hover:-translate-y-0.5 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold/60"
      data-testid={`course-card-${course.slug}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={course.thumbnail} alt={course.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-cream-100/85 via-cream-100/30 to-transparent" />
        {discount > 0 && (
          <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold bg-gold text-black">{discount}% OFF</span>
        )}
        {course.offer?.name && (
          <span className="absolute top-4 right-4 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] glass-strong">
            {course.offer.name}
          </span>
        )}
        <div className="absolute bottom-4 right-4 w-9 h-9 rounded-full bg-cream-50/95 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight className="w-4 h-4 text-ink-900" />
        </div>
      </div>
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm text-ink-600">
          <Star className="w-4 h-4 text-gold" />
          <span className="text-ink-800">{course.rating || 4.9}</span>
          <span className="text-ink-400">•</span>
          <BookOpen className="w-4 h-4" />
          <span>{course.lessons_count || 0} lessons</span>
          <span className="text-ink-400">•</span>
          <Clock className="w-4 h-4" />
          <span>{course.duration}</span>
        </div>
        <h3 className="font-display text-2xl font-bold leading-tight group-hover:text-gold transition-colors">{course.title}</h3>
        <p className="text-ink-600 leading-relaxed text-sm">{course.short_description}</p>
        <div className="flex items-baseline gap-3">
          <span className="text-2xl font-display font-bold text-ink-900">{money(course.current_price)}</span>
          {course.original_price > course.current_price && (
            <span className="text-ink-500 line-through text-sm">{money(course.original_price)}</span>
          )}
        </div>
        {course.offer?.ends_at && (
          <div className="pt-2">
            <div className="overline mb-2">Offer ends in</div>
            <Countdown endsAt={course.offer.ends_at} compact />
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <Link
            to={`/courses/${course.slug}`}
            onClick={(e) => e.stopPropagation()}
            className="btn-ghost flex-1 text-sm !py-2.5"
            data-testid={`view-course-${course.slug}`}
          >View course</Link>
          <Link
            to={`/checkout/${course.slug}`}
            onClick={(e) => e.stopPropagation()}
            className="btn-neon flex-1 text-sm !py-2.5"
            data-testid={`enroll-course-${course.slug}`}
          >Enroll now</Link>
        </div>
      </div>
    </div>
  );
}
